import type { ProblemData } from "@/components/ProblemDataContext";
import { getProblemsFromDb, saveProblemsToDb } from "@/lib/problemDb";
import { getProblemsBatch } from "@/lib/server-actions/problem/read";
import { handleServerActionError } from "@/lib/serverActionUtils";
import { withTimeout } from "@/lib/with-timeout";

const CACHE_READ_TIMEOUT_MS = 1500;
const PROBLEM_REQUEST_TIMEOUT_MS = 15000;

/** 캐시는 보조 수단이다. 브라우저 저장소가 멈춰도 문제 조회와 렌더링은 계속한다. */
export async function loadProblems(
  ids: string[],
  usePersistentCache: boolean,
): Promise<Record<string, ProblemData | null>> {
  if (ids.length === 0) return {};

  let cached: Awaited<ReturnType<typeof getProblemsFromDb>> = {
    hits: {},
    misses: ids,
  };
  let canSaveCache = usePersistentCache;
  if (usePersistentCache) {
    try {
      cached = await withTimeout(
        () => getProblemsFromDb(ids),
        CACHE_READ_TIMEOUT_MS,
        "문제 캐시 읽기 시간이 초과되었습니다.",
      );
    } catch (error) {
      canSaveCache = false;
      console.warn("[loadProblems] 캐시를 건너뛰고 서버에서 조회합니다:", error);
    }
  }

  if (cached.misses.length === 0) return cached.hits;

  let result: Awaited<ReturnType<typeof getProblemsBatch>>;
  try {
    result = await withTimeout(
      () => getProblemsBatch(cached.misses),
      PROBLEM_REQUEST_TIMEOUT_MS,
      "문제를 불러오는 데 시간이 오래 걸리고 있어요. 다시 시도해 주세요.",
    );
  } catch (error) {
    handleServerActionError(error);
    // 실패를 빈 데이터로 캐싱하면 재시도 없이 빈 화면에 머무르게 된다.
    throw error;
  }

  if (!result.success) {
    throw new Error(result.error || "문제를 불러오지 못했어요.");
  }

  const serverProblems = result.problems as Record<string, ProblemData | null>;
  if (canSaveCache) {
    // 쓰기 완료를 기다리면 이미 받은 문제도 IndexedDB 지연에 가려진다.
    void saveProblemsToDb(serverProblems).catch((error) => {
      console.warn("[loadProblems] 문제 캐시 저장 실패:", error);
    });
  }

  return { ...cached.hits, ...serverProblems };
}
